"use client";

import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { buildAppUrl, resolveAppBaseUrl, type AppConfig } from '../config/get-config';
import { checkAuthenticatedUser } from '../hooks/use-authenticated-user';
import { persistAuthenticatedUserSession } from '../services/auth-storage';
import { authDestination, authRequest, HomeAuthError, registerEmail, resendVerification, verifiedSession, type AuthResponse } from '../services/home-auth';
import { loadGoogle } from '../services/home-social-auth';
import { startAppleRedirect, consumeAppleRedirect } from '../services/home-apple-redirect';
import { linkAcquisitionVisits } from '../services/home-acquisition';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { validateAuthFields, type AuthMode as Mode, type AuthField, type AuthFieldErrors } from '../services/home-auth-validation';

type Intent = { mode: 'signup' | 'login'; href: string };

function AuthForm({ config, intent, initialError = '' }: { config: AppConfig; intent: Intent; initialError?: string }) {
  const [mode, setMode] = useState<Mode>(intent.mode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError);
  const [notice, setNotice] = useState('');
  const [fieldErrors, setFieldErrors] = useState<AuthFieldErrors>({});
  const [attempted, setAttempted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [cooldown, setCooldown] = useState(0);
  const [googleError, setGoogleError] = useState('');
  const [providerRetry, setProviderRetry] = useState(0);
  const googleElement = useRef<HTMLDivElement>(null);
  const alive = useRef(true);
  const pending = useRef(false);
  const showSocial = mode === 'signup' || mode === 'login';

  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(() => setCooldown(value => Math.max(0, value - 1)), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const complete = useCallback(async (response: AuthResponse, userEmail = '') => {
    if (!alive.current) return;
    persistAuthenticatedUserSession(verifiedSession(response, userEmail));
    await linkAcquisitionVisits();
    if (!alive.current) return;
    window.location.assign(intent.href);
  }, [intent.href]);

  const run = useCallback(async (action: () => Promise<void>) => {
    if (pending.current || !alive.current) return;
    pending.current = true;
    setBusy(true); setError(''); setNotice('');
    try { await action(); }
    catch (cause) {
      if (alive.current) setError(cause instanceof HomeAuthError ? cause.message : 'Unable to complete this step. Please try again.');
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!showSocial) return;
    let active = true;
    if (config.googleWebClientId) {
      void loadGoogle().then(google => {
        if (!active || !googleElement.current) return;
        google.cancel();
        google.initialize({ client_id: config.googleWebClientId, auto_select: false,
          callback: response => {
            if (!active || !response.credential) return;
            void run(async () => complete(await authRequest('auth/google', { accessToken: response.credential })));
          },
        });
        google.renderButton(googleElement.current, { theme: 'outline', size: 'large', text: 'continue_with', shape: 'pill', width: Math.min(360, googleElement.current.clientWidth) });
      }).catch(() => { if (active) setGoogleError('Google sign in could not load.'); });
    }
    return () => { active = false; };
  }, [config.googleWebClientId, config.appleWebClientId, showSocial, run, complete, providerRetry]);

  function changeMode(next: Mode) {
    setMode(next); setError(''); setNotice(''); setPassword(''); setOtp('');
    setFieldErrors({}); setAttempted(false);
  }
  function updateField(field: AuthField, value: string) {
    if (field === 'email') setEmail(value);
    if (field === 'password') setPassword(value);
    if (field === 'otp') setOtp(value);
    setError('');
    if (attempted) setFieldErrors(validateAuthFields(mode, { email, password, otp, [field]: value }));
  }
  function fieldFeedback(field: AuthField) {
    return fieldErrors[field] ? <span id={`home-auth-${field}-error`} role="status" className="flex items-start gap-2 px-2 text-[13px] font-medium leading-5 text-rose-700"><CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />{fieldErrors[field]}</span> : null;
  }
  async function submit() {
    const address = email.trim();
    if (mode === 'signup') {
      await registerEmail(address, password);
      if (alive.current) { setEmail(address); setPassword(''); setMode('verify'); setCooldown(60); }
    } else if (mode === 'login') {
      try { await complete(await authRequest('authenticate-password', { emailOrMobile: address, password }), address); }
      catch (cause) {
        if (!(cause instanceof HomeAuthError) || cause.code !== 'USER_EMAIL_NOT_ACTIVATED') throw cause;
        await resendVerification(address);
        if (alive.current) { setPassword(''); setMode('verify'); setCooldown(60); }
      }
    } else if (mode === 'verify') {
      await complete(await authRequest('verify-email-or-mobile-otp', { emailOrMobile: address, otp }), address);
    } else if (mode === 'forgot') {
      await authRequest('forgot-password', { emailOrMobile: address });
      if (alive.current) { setMode('reset'); setCooldown(60); }
    } else {
      await authRequest('reset-password', { emailOrMobile: address, otp, password });
      if (alive.current) { changeMode('login'); setNotice('Password reset. Sign in with your new password.'); }
    }
  }

  const titles = { signup: 'Create your free account', login: 'Welcome back', verify: 'Verify your email', forgot: 'Reset your password', reset: 'Set a new password' };
  const labels = { signup: 'Create account', login: 'Sign in', verify: 'Verify & continue', forgot: 'Send reset code', reset: 'Save password' };
  const needsCode = mode === 'verify' || mode === 'reset';
  const needsPassword = showSocial || mode === 'reset';
  return <>
    <DialogTitle className="pr-5 text-center text-[22px] font-bold">{titles[mode]}</DialogTitle>
    <DialogDescription className="text-center">
      {needsCode ? `Enter the 4-digit code sent to ${email}.` : mode === 'forgot' ? 'We will email you a code to reset your password.' : 'Continue to PTE Ultra and start your practice.'}
    </DialogDescription>
    {showSocial && <div className="grid gap-3" inert={busy}>
      <div ref={googleElement} className={config.googleWebClientId && !googleError ? 'flex min-h-11 w-full justify-center' : 'hidden'} />
      {(!config.googleWebClientId || googleError) && <><Button type="button" variant="outline" disabled className="h-11 rounded-full">Continue with Google</Button><p className="text-center text-sm">{googleError || 'Google sign in is unavailable in this preview.'}</p></>}
      <Button type="button" variant="outline" className="h-11 rounded-full" disabled={busy || !config.appleWebClientId} onClick={() => {
        void run(async () => {
          startAppleRedirect(config.appleWebClientId!, config.appleRedirectUri || `${config.apiUrl.replace(/\/+$/, '')}/user/auth/apple/callback`, { ...intent, mode: mode === 'signup' ? 'signup' : 'login' });
        });
      }}>Continue with Apple</Button>
      {googleError && <button type="button" className="text-sm underline" onClick={() => { setGoogleError(''); setProviderRetry(value => value + 1); }}>Retry sign-in providers</button>}
      <div className="flex items-center gap-3 text-sm text-muted-foreground"><span className="h-px flex-1 bg-border" />Or<span className="h-px flex-1 bg-border" /></div>
    </div>}
    <form ref={formRef} noValidate onSubmit={event => {
      event.preventDefault();
      const errors = validateAuthFields(mode, { email, password, otp });
      setAttempted(true); setFieldErrors(errors); setError('');
      const firstInvalid = (['email', 'otp', 'password'] as const).find(field => errors[field]);
      if (firstInvalid) { formRef.current?.querySelector<HTMLInputElement>(`[name="${firstInvalid}"]`)?.focus(); return; }
      void run(submit);
    }}>
      <fieldset disabled={busy} className="grid min-w-0 gap-4">
        {!needsCode && <label className="grid gap-2 text-sm font-bold">Email
          <Input name="email" aria-invalid={Boolean(fieldErrors.email)} aria-describedby={fieldErrors.email ? 'home-auth-email-error' : undefined} className="h-12 rounded-full font-normal aria-invalid:border-rose-400 aria-invalid:bg-rose-50/50 aria-invalid:ring-rose-200" type="email" autoComplete="email" required maxLength={254} value={email} onChange={event => updateField('email', event.target.value)} />
          {fieldFeedback('email')}
        </label>}
        {needsCode && <label className="grid gap-2 text-sm font-bold">Verification code
          <Input name="otp" aria-invalid={Boolean(fieldErrors.otp)} aria-describedby={fieldErrors.otp ? 'home-auth-otp-error' : undefined} className="h-12 rounded-full text-center text-xl tracking-[0.4em] aria-invalid:border-rose-400 aria-invalid:bg-rose-50/50 aria-invalid:ring-rose-200" autoFocus inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{4}" maxLength={4} required value={otp} onChange={event => updateField('otp', event.target.value.replace(/\D/g, '').slice(0, 4))} />
          {fieldFeedback('otp')}
        </label>}
        {needsPassword && <label className="grid gap-2 text-sm font-bold">{mode === 'reset' ? 'New password' : 'Password'}
          <div className="relative"><Input name="password" aria-invalid={Boolean(fieldErrors.password)} aria-describedby={fieldErrors.password ? 'home-auth-password-error' : undefined} className="h-12 rounded-full pr-16 font-normal aria-invalid:border-rose-400 aria-invalid:bg-rose-50/50 aria-invalid:ring-rose-200" type={visiblePassword ? 'text' : 'password'} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} required value={password} onChange={event => updateField('password', event.target.value)} />
            <button type="button" aria-label={visiblePassword ? 'Hide password' : 'Show password'} aria-pressed={visiblePassword} className="absolute inset-y-0 right-4 text-sm font-normal" onClick={() => setVisiblePassword(value => !value)}>{visiblePassword ? 'Hide' : 'Show'}</button>
          </div>
          {fieldFeedback('password')}
        </label>}
        {error && <div role="alert" className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-5 text-rose-800"><CircleAlert className="mt-0.5 size-5 shrink-0" aria-hidden="true" /><p>{error}</p></div>}
        {notice && <div role="status" className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-5 text-emerald-800"><CircleCheck className="mt-0.5 size-5 shrink-0" aria-hidden="true" /><p>{notice}</p></div>}
        <Button type="submit" className="h-12 rounded-full bg-secondary text-white">{busy ? 'Please wait…' : labels[mode]}</Button>
        {mode === 'login' && <button type="button" className="text-sm underline" onClick={() => changeMode('forgot')}>Forgot password?</button>}
        {needsCode && <button type="button" disabled={cooldown > 0} className="text-sm underline disabled:opacity-50" onClick={() => void run(async () => {
          if (mode === 'verify') await resendVerification(email.trim());
          else await authRequest('forgot-password', { emailOrMobile: email.trim() });
          if (alive.current) { setCooldown(60); setNotice('A new code has been sent.'); }
        })}>{cooldown ? `Resend code in ${cooldown}s` : 'Resend code'}</button>}
        <button type="button" className="text-sm underline" onClick={() => changeMode(mode === 'login' ? 'signup' : 'login')}>
          {mode === 'login' ? 'New to PTE Ultra? Create an account' : 'Already have an account? Sign in'}
        </button>
        {needsCode && <button type="button" className="text-sm underline" onClick={() => changeMode(mode === 'verify' ? 'signup' : 'forgot')}>Use a different email</button>}
        {mode === 'signup' && <p className="text-center text-xs text-muted-foreground">By creating an account, you agree to our <a href={buildAppUrl('/privacy-policy', config)} target="_blank" rel="noopener noreferrer" data-auth-bypass className="underline">Privacy Policy</a> and <a href={buildAppUrl('/terms-and-conditions', config)} target="_blank" rel="noopener noreferrer" data-auth-bypass className="underline">Terms & Conditions</a>.</p>}
      </fieldset>
    </form>
  </>;
}

export default function HomeAuthModal({ config }: { config: AppConfig }) {
  const [intent, setIntent] = useState<Intent | null>(null);
  const [checkingSession, setCheckingSession] = useState(false);
  const [redirectError, setRedirectError] = useState('');
  const [sessionCheckFailed, setSessionCheckFailed] = useState(false);
  const appleReturn = useRef<ReturnType<typeof consumeAppleRedirect>>(null);
  const appleCompletion = useRef<Promise<void> | null>(null);
  const trigger = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const result = appleReturn.current ?? consumeAppleRedirect(resolveAppBaseUrl(config));
    if (!result) return;
    appleReturn.current = result;
    setIntent(result.intent);
    if (!result.payload) { setRedirectError(result.error); setCheckingSession(false); return; }
    setCheckingSession(true);
    // Retain one exchange across Strict Mode effect replays: Apple codes are single-use.
    appleCompletion.current ??= (async () => {
      const response = await authRequest('auth/apple', result.payload);
      persistAuthenticatedUserSession(verifiedSession(response));
      await linkAcquisitionVisits();
      window.location.assign(result.intent.href);
    })();
    let active = true;
    void appleCompletion.current.catch(cause => {
      if (!active) return;
      setRedirectError(cause instanceof HomeAuthError ? cause.message : 'Apple sign in could not be completed. Please try again.');
      setCheckingSession(false);
    });
    return () => { active = false; };
  }, [config]);
  useEffect(() => {
    if (!intent || appleReturn.current) return;
    let active = true;
    setSessionCheckFailed(false);
    void checkAuthenticatedUser().then(async authenticated => {
      if (!active) return;
      if (authenticated) {
        await linkAcquisitionVisits();
        if (!active) return;
        window.location.assign(intent.href);
      } else {
        setCheckingSession(false);
      }
    }).catch(() => {
      if (!active) return;
      setCheckingSession(false);
      setSessionCheckFailed(true);
    });
    return () => { active = false; };
  }, [intent]);

  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (event.defaultPrevented || event.button !== 0) return;
      const anchor = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null;
      if (!anchor || anchor.hasAttribute('data-auth-bypass') || anchor.hasAttribute('download')) return;
      const clickedUrl = new URL(anchor.href);
      if (clickedUrl.origin === window.location.origin && !clickedUrl.searchParams.has('ptu_source') && !['/login', '/signup'].includes(clickedUrl.pathname)) return;
      const target = authDestination(anchor.href, resolveAppBaseUrl(config), window.location.href);
      if (!target) return;
      // Legal/informational links are not account-entry CTAs.
      if (/\/(privacy|terms|payment-refund-policy|contact|about|blog)/i.test(clickedUrl.pathname)) return;
      event.preventDefault();
      trigger.current = anchor;
      appleReturn.current = null;
      setRedirectError('');
      setCheckingSession(true);
      setIntent(target);
    }
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [config]);
  return <Dialog open={Boolean(intent)} onOpenChange={open => { if (!open) setIntent(null); }}>
    <DialogContent className="rr-block max-h-[90dvh] overflow-y-auto rounded-[30px] sm:max-w-[440px]" data-auth-private onCloseAutoFocus={event => { event.preventDefault(); trigger.current?.focus(); }}>
      {checkingSession ? <>
        <DialogTitle className="text-center">Checking your session…</DialogTitle>
        <DialogDescription role="status" className="text-center">Please wait while we check whether you are already signed in.</DialogDescription>
        <div aria-hidden="true" className="mx-auto w-3/5 space-y-3 overflow-hidden">
          {[0, 1, 2, 3].map(line => <div key={line} className="h-3 rounded bg-gray-100 overflow-hidden">
            <div className="h-full w-full animate-[auth-shimmer_1.5s_linear_infinite] bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
          </div>)}
          <style>{'@keyframes auth-shimmer { from { transform: translateX(-100%); } to { transform: translateX(100%); } }'}</style>
        </div>
      </> : sessionCheckFailed ? <>
        <DialogTitle className="text-center">Unable to check your login</DialogTitle>
        <DialogDescription className="text-center">Check your connection and try again.</DialogDescription>
        <button type="button" className="rounded-full border px-5 py-3 font-semibold" onClick={() => {
          setCheckingSession(true); setIntent(value => value ? { ...value } : null);
        }}>Retry</button>
      </> : intent && <AuthForm config={config} intent={intent} initialError={redirectError} />}
    </DialogContent>
  </Dialog>;
}
